/**
 * Air temperature by elevation: a straight line through two user-set anchor points.
 *
 * Inversions are deliberately not modelled (see PLAN.md §1). Everything reads temperature
 * through `temperatureAt`, so a piecewise profile can replace this without touching callers.
 */
export interface TemperatureProfile {
  lowElevation: number;
  lowTemp: number;
  highElevation: number;
  highTemp: number;
}

export const DEFAULT_TEMPERATURE: TemperatureProfile = {
  lowElevation: 0,
  lowTemp: 2,
  highElevation: 1500,
  highTemp: -10,
};

/** Typical environmental lapse rates, °C cooling per 100 m. */
export const TYPICAL_LAPSE_RATE = { min: 0.5, max: 1.0 } as const;

/** Cooling per 100 m of ascent (positive = colder with height). */
export function lapseRatePer100m(p: TemperatureProfile): number {
  const dz = p.highElevation - p.lowElevation;
  if (dz === 0) return 0;
  return ((p.lowTemp - p.highTemp) / dz) * 100;
}

export function temperatureAt(p: TemperatureProfile, elevation: number): number {
  const dz = p.highElevation - p.lowElevation;
  if (dz === 0) return p.lowTemp;
  return p.lowTemp + ((elevation - p.lowElevation) / dz) * (p.highTemp - p.lowTemp);
}

/** Elevation where the profile crosses `temp`, or null if it is flat. */
export function elevationOfTemperature(p: TemperatureProfile, temp: number): number | null {
  if (p.highTemp === p.lowTemp) return null;
  return (
    p.lowElevation +
    ((temp - p.lowTemp) / (p.highTemp - p.lowTemp)) * (p.highElevation - p.lowElevation)
  );
}

/**
 * Rain/snow partitioning by air temperature: all snow at ≤ 0 °C, all rain at ≥ +2 °C, linear
 * mix in between. Returns the snow fraction of precipitation, 0…1.
 */
export const RAIN_SNOW_BAND = { allSnowBelow: 0, allRainAbove: 2 } as const;

export function snowFraction(airTemp: number): number {
  const { allSnowBelow, allRainAbove } = RAIN_SNOW_BAND;
  if (airTemp <= allSnowBelow) return 1;
  if (airTemp >= allRainAbove) return 0;
  return (allRainAbove - airTemp) / (allRainAbove - allSnowBelow);
}
